import React from 'react';
import { graphql, useFragment } from 'react-relay';
import Avatar from '@atlaskit/avatar';
import { Flex } from '@atlaskit/primitives';
import type { readViewUserField_servicedeskCustomerServiceCustomDetails$key } from '@atlassian/jira-relay/src/__generated__/readViewUserField_servicedeskCustomerServiceCustomDetails.graphql';
import { ProfileCard } from '@atlassian/jira-servicedesk-customers-organization-common';
import type { ReadViewProps } from '../../types';

export const UserFieldReadView = ({
	userFieldRef,
	render,
	getValueContainer,
	inInlineEdit,
}: {
	userFieldRef: readViewUserField_servicedeskCustomerServiceCustomDetails$key;
} & Pick<ReadViewProps, 'render' | 'getValueContainer' | 'inInlineEdit'>) => {
	const { platformValue } = useFragment(
		graphql`
			fragment readViewUserField_servicedeskCustomerServiceCustomDetails on CustomerServiceCustomDetailValue
			@argumentDefinitions(includePlatformValue: { type: "Boolean!" }) {
				platformValue @include(if: $includePlatformValue) {
					__typename
					... on CustomerServiceUserDetailValue {
						accountId
						name
						avatarUrl
					}
				}
			}
		`,
		userFieldRef || null,
	);

	const ValueContainer = getValueContainer();

	if (
		!platformValue ||
		platformValue.__typename !== 'CustomerServiceUserDetailValue' ||
		// BE returns a user with accountId undefined, name 'Unknown user', avatarUrl '.../unknown-user.png' if the user can't be retrieved from identity
		!platformValue.name
	) {
		return <ValueContainer render={render || (() => null)} noBorder={inInlineEdit} />;
	}

	const { accountId, name, avatarUrl } = platformValue;
	return (
		<ValueContainer
			render={
				render ||
				(() => (
					<Flex
						gap="space.100"
						alignItems="center"
						testId="servicedesk-customer-service-custom-details.common.ui.field.user-field.read-view.user-field"
					>
						<ProfileCard accountId={accountId || ''}>
							<Avatar src={avatarUrl || ''} size="small" />
						</ProfileCard>
						{name}
					</Flex>
				))
			}
			noBorder={inInlineEdit}
		/>
	);
};
